/**
 * Bundle 51 - Resume link handler.
 *
 * Route: `/rsvp/resume/:token`
 *
 * The 2h / 24h / 72h reminder emails carry a one-time token. This
 * page swaps the token for a short-lived session JWT (via
 * `GET /api/rsvp/resume/:token`), hydrates the auth context, then
 * navigates straight to the profile-completion gate for the
 * matching registration.
 *
 * Invalid or expired tokens surface a plain-English error.
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';
import { useAuth } from '@/contexts/AuthContext';

const RsvpResumePage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { setSessionFromToken } = useAuth();
  const [err, setErr] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/rsvp/resume/${encodeURIComponent(token)}`);
        if (setSessionFromToken) {
          await setSessionFromToken(data.access_token);
        } else {
          localStorage.setItem('access_token', data.access_token);
          localStorage.setItem('auth_token', data.access_token);
        }
        toast.success('Signed in. Let us finish your RSVP.');
        navigate(`/rsvp/${data.registration_id}/complete-profile`, { replace: true });
      } catch (e) {
        const d = e?.response?.data?.detail;
        setErr(typeof d === 'string' ? d : d?.message || 'This link is not valid.');
      }
    })();
  }, [token, navigate, setSessionFromToken]);

  if (err) {
    return (
      <div className="max-w-md mx-auto py-16 px-4 text-center" data-testid="rsvp-resume-error">
        <XCircle className="h-10 w-10 mx-auto text-red-600 mb-3" />
        <h1 className="font-display text-2xl font-bold text-primary-900 mb-2">
          Link no longer works
        </h1>
        <p className="text-sm text-neutral-700">{err}</p>
        <p className="text-xs text-neutral-500 mt-4">
          Ask the event organizer to resend, or sign in and open your RSVP from
          your dashboard.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto py-16 text-center text-neutral-500"
          data-testid="rsvp-resume-loading">
      <Loader2 className="h-6 w-6 mx-auto mb-3 animate-spin" />
      Signing you in...
    </div>
  );
};

export default RsvpResumePage;
